import React, { useEffect, useRef, useState } from "react";
import { Pencil, Link2, Archive, Trash2, Check, GitBranch } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import PixelAvatar from "@/shared/components/PixelAvatar";
import Logo from "@/shared/components/Logo";
import { sharedChatUrl, toMessages, useFirstMessages, useShareThread, useUnshareThread } from "@/shared/api/threads";
import { PinIcon } from "@/shared/components/icons/animated";
import type { Agent, Chat, Message } from "@/shared/types";

const item =
  "flex items-center gap-2 px-2 py-1.5 rounded-[7px] text-[12px] tracking-[-0.005em] cursor-pointer text-[var(--text-primary)] focus:bg-[var(--accent-hover)]";

/* ---------- Pin ---------- */

// Pinned-chat mark — lucide's pin, sized to match the New-chat plus (14px).
// Tilts on hover (PinIcon's own variant); pops in when a chat gets pinned
// (.pin-pop). Re-exported here so the chat surfaces all pull one mark.
export function ChatPin({ size = 14, pop = false, className = "" }: {
  size?: number;
  pop?: boolean;
  className?: string;
}) {
  return (
    <PinIcon
      size={size}
      strokeWidth={1.75}
      className={`${pop ? "pin-pop" : ""} ${className}`}
    />
  );
}

/* ---------- Share dialog ---------- */

type SvgProps = React.SVGProps<SVGSVGElement>;

const XLogo = (props: SvgProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
  </svg>
);

const LinkedInLogo = (props: SvgProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
  </svg>
);

const RedditLogo = (props: SvgProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595zM8.208 12.617c-.884 0-1.601.717-1.601 1.6s.717 1.601 1.601 1.601 1.6-.717 1.6-1.6-.717-1.601-1.6-1.601zm7.584 0c-.884 0-1.6.717-1.6 1.6s.716 1.601 1.6 1.601 1.6-.717 1.6-1.6-.716-1.601-1.6-1.601zm-3.792 4.821c-1.19 0-2.307-.149-3.336-.416-.203-.053-.401.11-.377.318.167 1.472 1.797 2.147 3.713 2.147 1.917 0 3.546-.675 3.713-2.147.024-.208-.174-.371-.377-.318-1.029.267-2.146.416-3.336.416z" />
  </svg>
);

// First user line and first agent text line — enough for a recognizable preview.
// The chat's messages are read from the API and handed in.
function previewMessages(chat?: Chat, read?: Message[]) {
  const msgs = read ?? chat?.messages ?? [];
  const user = msgs.find((m) => m.role === "user");
  const agent = msgs.find((m) => m.role === "agent");
  return {
    user: user?.text,
    agent: agent?.text || agent?.blocks?.find((b) => b.type === "text")?.text,
  };
}

function ShareTarget({ label, onClick, children }: {
  label: string;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <button onClick={onClick} className="press group flex flex-col items-center gap-2">
      <span className="w-11 h-11 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] flex items-center justify-center text-[var(--text-primary)] group-hover:bg-[var(--accent-hover)] transition-colors">
        {children}
      </span>
      <span className="text-[10.5px] text-[var(--text-secondary)] tracking-[-0.005em]">{label}</span>
    </button>
  );
}

export function ShareDialog({ open, onOpenChange, agent, chat }: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  agent: Agent;
  chat?: Chat;
}) {
  const [copied, setCopied] = useState(false);
  // A chat gets a public page of its own once it is shared.
  const server = !!chat;
  const { data: rows } = useFirstMessages(open && server ? chat.id : null);
  const share = useShareThread();
  const unshare = useUnshareThread();
  const url = server ? chat.shareToken && sharedChatUrl(chat.shareToken) : null;
  const { user, agent: agentText } = previewMessages(chat, rows && toMessages(rows));

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const copy = () => {
    if (!url) return;
    navigator.clipboard?.writeText(url);
    setCopied(true);
    toast.success("Link copied to clipboard");
  };
  const openShare = (base: string) => url && window.open(base + encodeURIComponent(url), "_blank", "noopener");
  const busy = share.isPending || unshare.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] p-0 gap-0 rounded-[14px] bg-[var(--canvas)] border border-[var(--hairline)] shadow-menu overflow-hidden">
        <div className="px-5 pt-4 pb-3">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] truncate pr-8">
            {chat?.title || "Share chat"}
          </DialogTitle>
          <p className="mt-0.5 text-[11.5px] text-[var(--text-tertiary)]">
            {server && !url
              ? "Create a public link to this conversation up to this point. Anyone with it can read it, no sign-in needed."
              : "Anyone with the link can view this conversation up to this point."}
          </p>
        </div>

        {/* Preview card — first exchange, fading out like a page peek */}
        <div className="px-5">
          <div className="relative rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] px-4 pt-4 pb-9 overflow-hidden">
            {user || agentText ? (
              <div className="space-y-3">
                {user && (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] px-3 py-2 rounded-[12px] rounded-br-[4px] bg-[var(--accent-active)] text-[12px] leading-[1.55] text-[var(--text-primary)] tracking-[-0.005em] line-clamp-3">
                      {user}
                    </div>
                  </div>
                )}
                {agentText && (
                  <div className="flex items-start gap-2.5">
                    <PixelAvatar seed={agent.seed} hue={agent.hue} size={20} ring rounded={5} />
                    <div className="min-w-0 text-[12px] leading-[1.55] text-[var(--text-secondary)] tracking-[-0.005em] line-clamp-4">
                      {agentText}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-4 text-center text-[11.5px] text-[var(--text-quaternary)]">
                No messages yet — the link will show an empty chat.
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[var(--surface)] to-transparent pointer-events-none" />
            <div className="absolute bottom-2.5 right-3 flex items-center gap-1.5 text-[var(--text-tertiary)]">
              <Logo size={13} />
              <span className="text-[11px] font-semibold tracking-[-0.01em]">Definable</span>
            </div>
          </div>
        </div>

        {server && !url && (
          <div className="flex justify-center px-5 py-5">
            <button
              onClick={() => share.mutate(chat.id)}
              disabled={busy}
              className="press h-8 px-3.5 rounded-[10px] bg-[var(--brand)] shadow-button text-[var(--brand-foreground)] text-[12px] font-medium hover:bg-[var(--brand-hover)] flex items-center gap-1.5 disabled:opacity-40 disabled:pointer-events-none"
            >
              <Link2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              Create link
            </button>
          </div>
        )}

        {/* Share targets */}
        {url && <div className="flex items-start justify-center gap-7 px-5 py-5">
          <ShareTarget label={copied ? "Copied" : "Copy link"} onClick={copy}>
            {copied ? (
              <Check className="w-4 h-4 text-[var(--success)]" strokeWidth={2} />
            ) : (
              <Link2 className="w-4 h-4" strokeWidth={1.75} />
            )}
          </ShareTarget>
          <ShareTarget label="X" onClick={() => openShare("https://twitter.com/intent/tweet?url=")}>
            <XLogo className="w-[15px] h-[15px]" />
          </ShareTarget>
          <ShareTarget label="LinkedIn" onClick={() => openShare("https://www.linkedin.com/sharing/share-offsite/?url=")}>
            <LinkedInLogo className="w-4 h-4" />
          </ShareTarget>
          <ShareTarget label="Reddit" onClick={() => openShare("https://www.reddit.com/submit?url=")}>
            <RedditLogo className="w-4 h-4" />
          </ShareTarget>
        </div>}

        {/* The link is a snapshot: newer messages wait for Update. */}
        {server && url && (
          <div className="flex items-center gap-2 px-5 py-3 border-t hairline">
            <span className="flex-1 text-[11px] text-[var(--text-tertiary)] tracking-[-0.005em]">
              Newer messages stay private until you update the link.
            </span>
            <button
              onClick={() => share.mutate(chat.id, { onSuccess: () => toast.success("Link updated") })}
              disabled={busy}
              className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[12px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)] disabled:opacity-40 disabled:pointer-events-none"
            >
              Update
            </button>
            <button
              onClick={() => unshare.mutate(chat.id, { onSuccess: () => toast.success("Link removed", { description: "It no longer opens this chat." }) })}
              disabled={busy}
              className="press rounded-full px-3.5 h-8 text-[12px] font-medium text-[var(--danger)] ring-1 ring-[var(--danger-ring)] hover:bg-[var(--danger-bg)] disabled:opacity-40 disabled:pointer-events-none"
            >
              Stop sharing
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Chat menu ---------- */

/* One menu for a chat, used by the sidebar row and the chat header.
   Delete is two-step inside the menu — first click arms it, second executes. */
export default function ChatMenu({ agent, chat, onRename, onTogglePin, onBranch, onArchive, onDelete, trigger, align = "start" }: {
  agent: Agent;
  chat?: Chat;
  /* The four below only ever reach radix's `onSelect`, which tolerates a gap;
     `onDelete` is called directly, so it is not optional. */
  onRename?: () => void;
  onTogglePin?: () => void;
  onBranch?: () => void;
  onArchive?: () => void;
  onDelete: () => void;
  trigger: React.ReactNode;
  align?: "start" | "center" | "end";
}) {
  const [confirming, setConfirming] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  return (
    <>
      <DropdownMenu onOpenChange={(open: boolean) => !open && setConfirming(false)}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className="w-[176px] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu"
        >
          <DropdownMenuItem className={item} onSelect={onTogglePin}>
            <ChatPin
              className={chat?.pinned ? "text-[var(--warn)]" : "text-[var(--text-tertiary)]"}
            />
            {chat?.pinned ? "Unpin" : "Pin"}
          </DropdownMenuItem>
          <DropdownMenuItem className={item} onSelect={onRename}>
            <Pencil className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            Rename
          </DropdownMenuItem>
          {/* Never on a channel conversation: it holds other people's messages, so it
              is not published; and a branch would be a typed chat passing itself off
              as the channel one. */}
          {!chat?.via && (
            <>
              {/* setTimeout: let the menu close and release focus before the dialog opens */}
              <DropdownMenuItem className={item} onSelect={() => setTimeout(() => setShareOpen(true), 0)}>
                <Link2 className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
                Share
              </DropdownMenuItem>
              <DropdownMenuItem className={item} onSelect={onBranch}>
                <GitBranch className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
                Branch chat
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator className="my-0.5" />
          <DropdownMenuItem className={item} onSelect={onArchive}>
            <Archive className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            Archive
          </DropdownMenuItem>
          <DropdownMenuSeparator className="my-0.5" />
          <DropdownMenuItem
            className={`${item} text-[var(--danger)] focus:bg-[var(--danger-bg)] focus:text-[var(--danger)]`}
            onSelect={(e: Event) => {
              if (!confirming) {
                e.preventDefault();
                setConfirming(true);
                return;
              }
              onDelete();
            }}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            {confirming ? "Confirm delete?" : "Delete"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} agent={agent} chat={chat} />
    </>
  );
}

/* Inline rename input — replaces the title where rename was triggered. */
export function RenameInput({ value, onSubmit, onCancel, className }: {
  value: string;
  onSubmit: (title: string) => void;
  onCancel: () => void;
  className?: string;
}) {
  const [v, setV] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = () => {
    const t = v.trim();
    if (t && t !== value) onSubmit(t);
    else onCancel();
  };
  return (
    <input
      ref={ref}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") onCancel();
      }}
      onClick={(e) => e.stopPropagation()}
      className={className}
    />
  );
}
